/**
 * Utilitaires pour la gestion des dates multiples
 */

/**
 * Formate une date de manière sécurisée (pas de RangeError si la valeur est invalide)
 * @param {*} value - Valeur de date (string, number, Date, null, undefined)
 * @param {string} locale - Locale (défaut: 'fr-FR')
 * @param {object} options - Options de toLocaleDateString
 * @param {string} fallback - Texte de repli si la date est invalide (défaut: '—')
 * @returns {string}
 */
export const safeFormatDate = (value, locale = 'fr-FR', options = {}, fallback = '—') => {
  if (value == null || value === '') return fallback;
  const d = new Date(value);
  if (isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString(locale, options);
};

/**
 * Formate une date+heure de manière sécurisée
 * @param {*} value - Valeur de date
 * @param {string} locale - Locale (défaut: 'fr-FR')
 * @param {object} options - Options de toLocaleString
 * @param {string} fallback - Texte de repli (défaut: '—')
 * @returns {string}
 */
export const safeFormatDateTime = (value, locale = 'fr-FR', options = {}, fallback = '—') => {
  if (value == null || value === '') return fallback;
  const d = new Date(value);
  if (isNaN(d.getTime())) return fallback;
  return d.toLocaleString(locale, options);
};

/**
 * Récupère toutes les dates d'un événement (date principale + dates multiples)
 * @param {Object} event - L'événement
 * @returns {Array} - Tableau de toutes les plages de dates
 */
export const getAllEventDates = (event) => {
  // Vérifier que l'événement existe et a des propriétés
  if (!event || !event.date) {
    return [];
  }
  
  const dates = [];
  
  // Ajouter la date principale si elle existe
  if (event.date?.start) {
    dates.push({
      start: event.date.start,
      end: event.date.end || event.date.start,
      isMain: true
    });
  }
  
  // Ajouter les dates multiples si elles existent
  if (event.dates_multiples && Array.isArray(event.dates_multiples)) {
    event.dates_multiples.forEach((dateRange, index) => {
      if (dateRange && dateRange.start) {
        dates.push({
          start: dateRange.start,
          end: dateRange.end || dateRange.start,
          isMain: false,
          periodIndex: index + 1
        });
      }
    });
  }
  
  return dates;
};

/**
 * Récupère toutes les dates d'un événement en filtrant les dates passées
 * @param {Object} event - L'événement
 * @param {boolean} hidePastDates - Si true, masque les dates déjà passées
 * @returns {Array} - Tableau de toutes les plages de dates (filtrées si hidePastDates est true)
 */
export const getAllEventDatesFiltered = (event, hidePastDates = false) => {
  // Vérifier que l'événement existe et a des propriétés
  if (!event || !event.date) {
    return [];
  }
  
  const dates = [];
  const now = new Date();
  
  // Ajouter la date principale si elle existe
  if (event.date?.start) {
    const endDate = new Date(event.date.end || event.date.start);
    if (!hidePastDates || endDate >= now || event.statut === 'En cours de configuration') {
      dates.push({
        start: event.date.start,
        end: event.date.end || event.date.start,
        isMain: true
      });
    }
  }
  
  // Ajouter les dates multiples si elles existent
  if (event.dates_multiples && Array.isArray(event.dates_multiples)) {
    event.dates_multiples.forEach((dateRange, index) => {
      if (dateRange && dateRange.start) {
        const endDate = new Date(dateRange.end || dateRange.start);
        if (!hidePastDates || endDate >= now || event.statut === 'En cours de configuration') {
          dates.push({
            start: dateRange.start,
            end: dateRange.end || dateRange.start,
            isMain: false,
            periodIndex: index + 1
          });
        }
      }
    });
  }
  
  return dates;
};

/**
 * Vérifie si une date donnée est dans les plages de dates d'un événement
 * @param {Object} event - L'événement
 * @param {Date|string} targetDate - La date à vérifier
 * @returns {boolean} - True si la date est dans une des plages
 */
export const isDateInEventRanges = (event, targetDate) => {
  // Vérifier que l'événement existe
  if (!event) {
    return false;
  }
  
  const allDates = getAllEventDates(event);
  const checkDate = new Date(targetDate);
  
  return allDates.some(dateRange => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    return checkDate >= start && checkDate <= end;
  });
};

/**
 * Récupère la première date de début d'un événement
 * @param {Object} event - L'événement
 * @returns {Date|null} - La première date de début
 */
export const getFirstEventDate = (event) => {
  const allDates = getAllEventDates(event);
  if (allDates.length === 0) return null;

  // Trier par date de début et retourner la plus ancienne
  allDates.sort((a, b) => new Date(a.start) - new Date(b.start));
  const date = new Date(allDates[0].start);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Récupère la dernière date de fin d'un événement
 * @param {Object} event - L'événement
 * @returns {Date|null} - La dernière date de fin
 */
export const getLastEventDate = (event) => {
  const allDates = getAllEventDates(event);
  if (allDates.length === 0) return null;
  
  // Trier par date de fin et retourner la plus récente
  allDates.sort((a, b) => new Date(b.end) - new Date(a.end));
  const date = new Date(allDates[0].end);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Vérifie si un événement est dans le passé
 * @param {Object} event - L'événement
 * @returns {boolean} - True si l'événement est terminé
 */
export const isEventPast = (event) => {
  const lastDate = getLastEventDate(event);
  if (!lastDate) return false;
  return lastDate < new Date();
};

/**
 * Vérifie si un événement est à venir
 * @param {Object} event - L'événement
 * @returns {boolean} - True si l'événement est à venir
 */
export const isEventUpcoming = (event) => {
  const firstDate = getFirstEventDate(event);
  if (!firstDate) return false;
  return firstDate > new Date();
};

/**
 * Vérifie si un événement est en cours
 * @param {Object} event - L'événement
 * @returns {boolean} - True si l'événement est en cours
 */
export const isEventOngoing = (event) => {
  const now = new Date();
  const allDates = getAllEventDates(event);
  
  return allDates.some(dateRange => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    return now >= start && now <= end;
  });
};

/**
 * Récupère la durée totale d'un événement (en jours)
 * @param {Object} event - L'événement
 * @returns {number} - Nombre total de jours
 */
export const getTotalEventDuration = (event) => {
  // Vérifier que l'événement existe
  if (!event) {
    return 0;
  }
  
  const allDates = getAllEventDates(event);
  
  return allDates.reduce((total, dateRange) => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 car on compte les jours inclus
    return total + diffDays;
  }, 0);
};

/**
 * Formate toutes les dates d'un événement pour l'affichage
 * @param {Object} event - L'événement
 * @returns {string} - Texte formaté de toutes les dates
 */
export const formatAllEventDates = (event) => {
  // Vérifier que l'événement existe
  if (!event) {
    return 'Aucune date définie';
  }
  
  const allDates = getAllEventDates(event);
  
  if (allDates.length === 0) return 'Aucune date définie';
  if (allDates.length === 1) {
    const date = allDates[0];
    return formatDateRange(date.start, date.end);
  }
  
  return allDates.map((dateRange, index) => {
    const prefix = dateRange.isMain ? 'Date principale' : `Période ${dateRange.periodIndex}`;
    return `${prefix}: ${formatDateRange(dateRange.start, dateRange.end)}`;
  }).join(' | ');
};

/**
 * Formate toutes les dates d'un événement pour l'affichage en masquant les dates passées
 * @param {Object} event - L'événement
 * @param {boolean} hidePastDates - Si true, masque les dates déjà passées
 * @returns {string} - Texte formaté de toutes les dates (filtrées si hidePastDates est true)
 */
export const formatAllEventDatesFiltered = (event, hidePastDates = false) => {
  // Vérifier que l'événement existe
  if (!event) {
    return 'Aucune date définie';
  }
  
  const allDates = getAllEventDatesFiltered(event, hidePastDates);
  
  if (allDates.length === 0) return 'Aucune date à venir';
  if (allDates.length === 1) {
    const date = allDates[0];
    return formatDateRange(date.start, date.end);
  }
  
  return allDates.map((dateRange, index) => {
    const prefix = dateRange.isMain ? 'Date principale' : `Période ${dateRange.periodIndex}`;
    return `${prefix}: ${formatDateRange(dateRange.start, dateRange.end)}`;
  }).join(' | ');
};

/**
 * Formate une plage de dates
 * @param {string} start - Date de début
 * @param {string} end - Date de fin
 * @returns {string} - Plage formatée
 */
const formatDateRange = (start, end) => {
  if (!start) return 'Non définie';
  const startDate = new Date(start).toLocaleDateString('fr-FR');
  if (!end || start === end) return `le ${startDate}`;
  const endDate = new Date(end).toLocaleDateString('fr-FR');
  return `du ${startDate} au ${endDate}`;
};

/**
 * Filtre les événements par période (passé, présent, futur)
 * @param {Array} events - Liste des événements
 * @param {string} period - 'past', 'ongoing', 'upcoming'
 * @returns {Array} - Événements filtrés
 */
export const filterEventsByPeriod = (events, period) => {
  const now = new Date();
  
  return events.filter(event => {
    const allDates = getAllEventDates(event);
    if (allDates.length === 0) return false;
    
    switch (period) {
      case 'past':
        return allDates.every(dateRange => new Date(dateRange.end) < now);
      case 'ongoing':
        return allDates.some(dateRange => {
          const start = new Date(dateRange.start);
          const end = new Date(dateRange.end);
          return now >= start && now <= end;
        });
      case 'upcoming':
        return allDates.every(dateRange => new Date(dateRange.start) > now);
      default:
        return true;
    }
  });
};

/**
 * Trie les événements par date (première date de début)
 * @param {Array} events - Liste des événements
 * @param {string} order - 'asc' ou 'desc'
 * @returns {Array} - Événements triés
 */
export const sortEventsByDate = (events, order = 'asc') => {
  return events.sort((a, b) => {
    const dateA = getFirstEventDate(a);
    const dateB = getFirstEventDate(b);
    
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    
    return order === 'asc' ? dateA - dateB : dateB - dateA;
  });
}; 